import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../api/client'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Coins, Bell, CreditCard, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react'
import { formatNumber, formatDateTime } from '../lib/utils'
import type { PointBalance, NotificationLog, NotificationStatistics } from '../types'

export function DashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [points, setPoints] = useState<PointBalance | null>(null)
  const [recentNotifications, setRecentNotifications] = useState<NotificationLog[]>([])
  const [statistics, setStatistics] = useState<NotificationStatistics | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pointsData, notificationData, statsData] = await Promise.all([
          apiClient.getPointBalance(),
          apiClient.getNotificationHistory(5, 0),
          apiClient.getNotificationStatistics(),
        ])
        setPoints(pointsData)
        setRecentNotifications(notificationData.history)
        setStatistics(statsData)
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold">
          {t('dashboard.welcome')}, {user?.name}!
        </h1>
        <p className="text-light-text-muted dark:text-dark-text-muted mt-1">
          ScreenEagle 대시보드에 오신 것을 환영합니다
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Points */}
        <Card className="relative overflow-hidden">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('dashboard.pointBalance')}
              </p>
              <p className="text-2xl font-bold mt-1">
                {formatNumber(points?.total_points || 0)}P
              </p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg">
              <Coins className="w-6 h-6 text-primary" />
            </div>
          </div>
          <Link
            to="/points"
            className="mt-4 text-sm text-primary hover:underline inline-flex items-center gap-1"
          >
            {t('dashboard.viewAll')} <ArrowRight className="w-4 h-4" />
          </Link>
        </Card>

        {/* Total Sent */}
        <Card className="relative overflow-hidden">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('notifications.totalSent')}
              </p>
              <p className="text-2xl font-bold mt-1">
                {formatNumber(statistics?.totalSent || 0)}
              </p>
            </div>
            <div className="p-3 bg-success/10 rounded-lg">
              <TrendingUp className="w-6 h-6 text-success" />
            </div>
          </div>
          <div className="mt-4 text-sm text-light-text-muted dark:text-dark-text-muted">
            성공률 {statistics?.totalSent ? Math.round(((statistics.totalSent - statistics.totalFailed) / statistics.totalSent) * 100) : 0}%
          </div>
        </Card>

        {/* Failed */}
        <Card className="relative overflow-hidden">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('notifications.failedCount')}
              </p>
              <p className="text-2xl font-bold mt-1">
                {formatNumber(statistics?.totalFailed || 0)}
              </p>
            </div>
            <div className="p-3 bg-danger/10 rounded-lg">
              <TrendingDown className="w-6 h-6 text-danger" />
            </div>
          </div>
          <div className="mt-4 text-sm text-light-text-muted dark:text-dark-text-muted">
            실패 알림 수
          </div>
        </Card>

        {/* Points Used */}
        <Card className="relative overflow-hidden">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('notifications.pointsUsed')}
              </p>
              <p className="text-2xl font-bold mt-1">
                {formatNumber(statistics?.totalPointsUsed || 0)}P
              </p>
            </div>
            <div className="p-3 bg-warning/10 rounded-lg">
              <CreditCard className="w-6 h-6 text-warning" />
            </div>
          </div>
          <Link
            to="/payments"
            className="mt-4 text-sm text-primary hover:underline inline-flex items-center gap-1"
          >
            {t('dashboard.quickCharge')} <ArrowRight className="w-4 h-4" />
          </Link>
        </Card>
      </div>

      {/* Recent Notifications */}
      <Card
        title={t('dashboard.recentNotifications')}
        action={
          <Link
            to="/notifications"
            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
          >
            {t('dashboard.viewAll')} <ArrowRight className="w-4 h-4" />
          </Link>
        }
      >
        {recentNotifications.length === 0 ? (
          <div className="text-center py-8 text-light-text-muted dark:text-dark-text-muted">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{t('notifications.noNotifications')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-light-border dark:border-dark-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                    {t('notifications.date')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                    {t('notifications.type')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                    {t('notifications.status')}
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                    {t('points.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentNotifications.map((notification) => (
                  <tr
                    key={notification.notificationId}
                    className="border-b border-light-border dark:border-dark-border last:border-0"
                  >
                    <td className="py-3 px-4 text-sm">
                      {formatDateTime(notification.sentAt)}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {notification.detectionMode}
                    </td>
                    <td className="py-3 px-4">
                      <Badge
                        variant={
                          notification.status === 'sent' || notification.status === 'delivered'
                            ? 'success'
                            : notification.status === 'pending'
                            ? 'warning'
                            : 'danger'
                        }
                      >
                        {notification.status === 'sent' ? t('notifications.sent') :
                         notification.status === 'delivered' ? t('notifications.delivered') :
                         notification.status === 'pending' ? t('notifications.pendingStatus') :
                         t('notifications.failedStatus')}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-right">
                      -{formatNumber(notification.pointsUsed)}P
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
